import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { CloudDownloadIcon } from '@sketchnote/icons';
import { Button, Input, LoaderXs } from '@sketchnote/shared-components';
import LuckyExcel from 'luckyexcel';
import { useCallback, useEffect, useState } from 'react';
import { useLayout } from 'src/shared/hooks/use-layout';
import { debug } from 'util';

import { getSheet } from './api/getSheet';
import { updateSheet } from './api/updateSheet';
import { SheetData } from './data/sheet';
import styles from './worksheet.module.scss';

const SheetView = ({ id }: { id: string }) => {
  const [sheetValue, setSheetValue] = useState<any>([]);
  const [fileData, setFileData] = useState<any>([]);
  const [fileName, setFileName] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const { splitView } = useLayout();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const createLuckySheet = useCallback((data) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    luckysheet.create({
      title: 'worksheet',
      container: 'luckysheet',
      showinfobar: false,
      plugins: [],
      hook: {
        updated: handleChange,
      },
      data,
      lang: 'en',
    });
  }, []);

  useEffect(() => {
    const fetchSheet = async (id) => {
      let data = SheetData;
      const response = await getSheet(id);
      if (response.data.data && response.data.data.length) data = response.data.data;
      createLuckySheet(data);
    };

    fetchSheet(id);
  }, []);

  const handleChange = async () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    setSheetValue(luckysheet.toJson().data);
    // await
  };

  useEffect(() => {
    const updateSheetValue = async () => {
      if (sheetValue.length) await updateSheet(id, sheetValue);
    };
    const sheetTimeout = setTimeout(() => {
      updateSheetValue();
    }, 1000);
    return () => clearTimeout(sheetTimeout);
  }, [sheetValue]);

  const updateFileData = async () => {
    if (fileData.length) {
      onClose();
      createLuckySheet(fileData);
      setFileName('');
      setFileData([]);
      await updateSheet(id, fileData);
    }
  };

  const handleClose = () => {
    onClose();
    setLoading(false);
    setFileData([]);
    setFileName('');
  };

  useEffect(() => {
    if (fileData.length) {
      setLoading(false);
    }
  }, [fileData]);

  return (
    <div
      style={{
        position: 'relative',
        top: splitView ? 0 : 40,
        left: 0,
        height: '83vh',
      }}
    >
      <>
        <div
          className="h-30 flex items-center px-22"
          style={{
            borderBottom: '1px solid #D5D7DD',
          }}
        >
          <div onClick={onOpen} className="flex gap-2 cursor-pointer">
            <CloudDownloadIcon fill="#878A92" className="transform scale-75 mt-1" />
            <div className="text-xs font-medium text-gray-999">Import Excel</div>
          </div>
          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent
              style={{ height: '367px', maxWidth: '460px', borderRadius: '4px', padding: '20px' }}
            >
              <ModalHeader>
                <h1 className=" text-xl font-regular text-gray-999">📥 Import</h1>
              </ModalHeader>
              <ModalCloseButton marginTop={14} marginRight={10} />
              <ModalBody className="flex flex-col gap-5">
                <div className="flex items-center justify-center w-full">
                  {isLoading ? (
                    <div style={{ height: '110px' }} className="flex justify-center items-center ">
                      <LoaderXs />
                    </div>
                  ) : (
                    <label
                      style={{ height: '110px' }}
                      htmlFor="dropzone-file"
                      className="flex flex-col items-center justify-center w-full border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                    >
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M22.6032 0.559657C22.2305 0.200605 21.7331 0 21.2156 0H7.97083C6.04074 0 4.48242 1.46562 4.48242 3.32299V13.3304H22.9731C25.2518 13.3304 27.1137 15.1001 27.1137 17.2931V24.7505C27.1137 26.9433 25.2518 28.747 22.9731 28.747H4.49317C4.5488 30.5504 6.07634 32 7.97083 32H28.5018C30.4317 32 31.9999 30.4761 31.9999 28.6184V10.4626C31.9999 9.91919 31.7788 9.3992 31.3875 9.02222L22.6032 0.559657ZM16.2652 9.51153H8.88549C8.47794 9.51153 8.14758 9.17815 8.14758 8.78589C8.14758 8.58965 8.22998 8.40503 8.36355 8.27673C8.49712 8.14817 8.68161 8.06205 8.88551 8.06205H16.2646C16.6721 8.06205 17.0025 8.39467 17.0025 8.78691C17.0028 9.17888 16.6728 9.51153 16.2652 9.51153ZM16.2986 5.23312H8.91949C8.51194 5.23312 8.18158 4.90833 8.18158 4.51608C8.18158 4.31984 8.26398 4.1486 8.39755 4.0203C8.53112 3.89175 8.71561 3.81901 8.91951 3.81901H16.2986C16.7061 3.81901 17.0365 4.13395 17.0365 4.52621C17.0365 4.91819 16.7061 5.23312 16.2986 5.23312ZM21.846 10.2187C21.5699 10.2187 21.346 9.99481 21.346 9.71867V3.55145C21.346 3.11092 21.8744 2.88567 22.1923 3.19072L28.6177 9.35794C28.9427 9.66983 28.7219 10.2187 28.2715 10.2187H21.846Z"
                            fill="#9BADEE"
                          />
                          <path
                            d="M25.3135 17.585C25.3135 16.1724 24.1237 15.0275 22.6562 15.0275H2.65732C1.18983 15.0275 0 16.1726 0 17.585V24.4922C0 25.9045 1.18983 27.0496 2.65732 27.0496H22.6562C24.124 27.0496 25.3135 25.9045 25.3135 24.4922V17.585ZM7.65788 23.6169C5.74741 23.6169 4.93916 22.3511 4.93184 21.0641C4.92449 19.7699 5.79887 18.4546 7.65788 18.4546C8.35595 18.4546 9.01721 18.7092 9.54626 19.2113L8.89964 19.8124C8.56165 19.4941 8.10611 19.3456 7.65788 19.3456C6.41612 19.3456 5.87969 20.2367 5.88708 21.0641C5.89443 21.8844 6.38673 22.7401 7.65788 22.7401C8.1061 22.7401 8.61312 22.5633 8.9511 22.238L9.61241 22.8815C9.08337 23.3836 8.40001 23.617 7.65787 23.617L7.65788 23.6169ZM12.1839 23.6311C11.1334 23.6311 10.3251 23.3199 9.94309 22.4148L10.7513 22.0117C10.9791 22.5562 11.5743 22.7967 12.1989 22.7967C12.8085 22.7967 13.3816 22.5916 13.3816 22.0542C13.3816 21.5874 12.8746 21.3965 12.1912 21.3257C11.1407 21.2055 10.1708 20.9368 10.1708 19.8265C10.1708 18.8082 11.2142 18.3909 12.1621 18.3839C12.9628 18.3839 13.7931 18.6031 14.1825 19.3669L13.411 19.7487C13.2347 19.4659 12.7497 19.1971 12.1913 19.1971C11.4714 19.1971 11.1261 19.4871 11.1261 19.8548C11.1261 20.2862 11.6552 20.4064 12.2724 20.4771C13.3449 20.6044 14.3442 20.8732 14.3442 22.0542C14.3442 23.1573 13.3302 23.6312 12.184 23.6312L12.1839 23.6311ZM17.8858 23.549H16.8792L14.7412 18.5634H15.8285L17.3789 22.4175L18.9367 18.5634H20.0242L17.8858 23.549Z"
                            fill="#9BADEE"
                          />
                        </svg>
                        <p className="mb-2 text-sm text-gray-500">{fileName}</p>
                      </div>
                      <input
                        id="dropzone-file"
                        type="file"
                        className="hidden"
                        onChange={(event) => {
                          setLoading(true);
                          const files = event.target.files;
                          if (files) {
                            LuckyExcel.transformExcelToLucky(
                              files[0],
                              function (exportJson, luckysheetfile) {
                                if (exportJson.sheets == null || exportJson.sheets.length === 0) {
                                  alert(
                                    'Failed to read the content of the excel file, currently does not support xls files!'
                                  );
                                  return;
                                }
                                setFileData(exportJson.sheets);
                                setFileName(exportJson.info.name);
                              }
                            );
                          }
                        }}
                      />
                    </label>
                  )}
                </div>

                <div className="text-xs flex justify-center font-medium text-gray-999 leading-18 mt-2">
                  Excel
                </div>
                <div className="text-xss flex justify-center font-regular leading-16 text-gray-600">
                  Select a Excel(.xlsx) file to import in the Worksheet
                </div>

                <div className="flex gap-8 justify-end mt-40">
                  <div
                    style={{ width: '119px' }}
                    onClick={handleClose}
                    className="cursor-pointer my-auto py-10 flex justify-around rounded-sm font-medium bg-white text-blue-500 border-blue-500 border-solid border-2 w-full items-center"
                  >
                    Cancel
                  </div>
                  <div
                    style={{ width: '119px' }}
                    onClick={updateFileData}
                    className="cursor-pointer my-auto py-12 flex justify-around rounded-sm font-medium bg-blue-500 text-white w-full items-center"
                  >
                    Continue
                  </div>
                </div>
              </ModalBody>
            </ModalContent>
          </Modal>
        </div>

        <div
          id="luckysheet"
          style={{
            margin: '0px',
            padding: '0px',
            position: 'absolute',
            height: '100%',
            width: '100%',
            fontFamily: 'Graphik Web',
          }}
        />
      </>
    </div>
  );
};

export default SheetView;
